import { convertCurrency as convertCurrencyEngine } from "../../../utils/financeEngine";

export const INVOICE_TYPE_OPTIONS = [
	{ value: "", label: "All" },
	{ value: "consultation", label: "Consultation" },
	{ value: "pharmacy", label: "Pharmacy" },
	{ value: "lab", label: "Lab" },
	{ value: "other_services", label: "Other Services" },
];

export const PAYMENT_METHOD_OPTIONS = [
	{ value: "cash", label: "Cash" },
	{ value: "online", label: "Online" },
	// { value: "card", label: "Card" },
	// { value: "upi", label: "UPI" },
	// { value: "bank_transfer", label: "Bank Transfer" },
	// { value: "wallet", label: "Wallet" },
];

export const formatDateDDMMYYYY = (dateString) => {
	if (!dateString) return "N/A";
	const date = new Date(dateString);
	if (Number.isNaN(date.getTime())) return "N/A";
	const day = date.getDate().toString().padStart(2, "0");
	const month = (date.getMonth() + 1).toString().padStart(2, "0");
	const year = date.getFullYear();
	return `${day}/${month}/${year}`;
};

export const formatDateList = (dateString) => {
	if (!dateString) return "N/A";
	const date = new Date(dateString);
	if (Number.isNaN(date.getTime())) return "N/A";
	const day = date.getDate().toString().padStart(2, "0");
	const month = date.toLocaleString(undefined, { month: "short" });
	const year = date.getFullYear();
	return `${day} ${month} ${year}`;
};

export const formatDateTime = (dateString) => {
	if (!dateString) return "N/A";
	const date = new Date(dateString);
	if (Number.isNaN(date.getTime())) return "N/A";
	const day = date.getDate().toString().padStart(2, "0");
	const month = date.toLocaleString(undefined, { month: "short" });
	const year = date.getFullYear();
	const hours = date.getHours().toString().padStart(2, "0");
	const minutes = date.getMinutes().toString().padStart(2, "0");
	return `${day} ${month} ${year}, ${hours}:${minutes}`;
};

export const formatInvoiceType = (type) => {
	if (!type) return "-";
	const map = {
		consultation: "Consultation",
		pharmacy: "Pharmacy",
		lab: "Lab",
		other_services: "Other Services",
		treatment_package: "Treatment Package",
	};
	return map[type] || type.replace(/_/g, " ");
};

export const normalizePaymentStatus = (status) => {
	if (!status) return "unpaid";
	const normalized = String(status).toLowerCase();
	if (normalized === "paid") return "paid";
	if (["partial_paid", "partially_paid", "partial"].includes(normalized)) return "partial_paid";
	return "unpaid";
};

const isAppointmentInvoiceSettled = (appointmentInvoice = {}) =>
	appointmentInvoice?.is_settled === true;

export const isPatientAccountSettled = (record) => {
	if (!record) return false;
	return isAppointmentInvoiceSettled(record.appointment_invoice || {});
};

export const formatAmount = (amount, currency, getCurrencySymbol) => {
	const value = Number(amount || 0).toFixed(2);
	if (getCurrencySymbol && currency) {
		return `${getCurrencySymbol(currency)} ${value}`;
	}
	return currency ? `${value} ${currency}` : value;
};

/**
 * Details-page only display rules:
 * - 1 item: Discount/Tax/Amount on that same row (Amount = base - discount + tax)
 * - 2+ items: item rows keep Amount = base; Discount/Tax empty;
 *   add a Subtotal row with Discount, Tax, and Amount = subtotal - discount + tax
 */
export const buildSectionLinesWithDiscountTax = (
	items = [],
	sectionDiscount = 0,
	sectionTax = 0,
	getBaseAmount = (item) => {
		const qty = Math.max(1, Number(item.quantity) || 1);
		const unit = Number(item.unit_price ?? item.price ?? item.charges ?? 0);
		const fromUnit = unit * qty;
		if (Number.isFinite(fromUnit) && fromUnit > 0) return fromUnit;
		return Number(item.amount ?? item.sub_total ?? 0) || 0;
	}
) => {
	const list = Array.isArray(items) ? items : [];
	const discount = Number(sectionDiscount) || 0;
	const tax = Number(sectionTax) || 0;
	const bases = list.map((item) => {
		const base = Number(getBaseAmount(item));
		return Number.isFinite(base) && base > 0 ? base : 0;
	});
	const subtotal = bases.reduce((sum, base) => sum + base, 0);
	const netTotal = Math.round((subtotal - discount + tax) * 100) / 100;
	const showSubtotalRow = discount > 0 || tax > 0;

	if (list.length === 0) {
		return {
			rows: [],
			showSubtotalRow: false,
			subtotal: 0,
			discount: 0,
			tax: 0,
			netTotal: 0,
		};
	}

	// Discount/tax always render as footer rows (not per-line columns).
	return {
		rows: list.map((item, index) => ({
			...item,
			line_base: bases[index],
			line_discount: 0,
			line_tax: 0,
			display_amount: bases[index],
			showLineDiscountTax: false,
		})),
		showSubtotalRow,
		subtotal,
		discount,
		tax,
		netTotal,
	};
};

/** @deprecated Use buildSectionLinesWithDiscountTax */
export const allocateSectionDiscountTaxToLines = (
	items = [],
	sectionDiscount = 0,
	sectionTax = 0,
	getBaseAmount
) => buildSectionLinesWithDiscountTax(items, sectionDiscount, sectionTax, getBaseAmount).rows;

/**
 * Resolve section discount/tax for invoice tables.
 * Prefer billing.summary when non-zero; otherwise appointment_invoice section fields
 * (consultation_* / laboratory_* / pharmacy_*), so empty billing still shows amounts.
 *
 * @param {"services"|"investigations"|"pharmacy"} section
 */
export const getInvoiceSectionDiscountTax = (record, section) => {
	const summary = record?.billing?.summary || {};
	const invoice = record?.appointment_invoice || {};

	if (section === "services") {
		return {
			discount:
				toNumber(summary.services_discount) ||
				toNumber(invoice.consultation_discount_amount),
			tax:
				toNumber(summary.services_tax) || toNumber(invoice.consultation_tax_amount),
			netTotal:
				toNumber(summary.services_total || summary.services_net_total) ||
				toNumber(invoice.consultation_total_amount),
		};
	}

	if (section === "investigations") {
		return {
			discount:
				toNumber(summary.investigation_discount) ||
				toNumber(invoice.laboratory_discount_amount),
			tax:
				toNumber(summary.investigation_tax) ||
				toNumber(invoice.laboratory_tax_amount),
			netTotal:
				toNumber(summary.investigation_total || summary.investigation_net_total) ||
				toNumber(invoice.laboratory_total_amount),
		};
	}

	if (section === "pharmacy") {
		return {
			discount:
				toNumber(summary.pharmacy_discount) ||
				toNumber(invoice.pharmacy_discount_amount),
			tax: toNumber(summary.pharmacy_tax) || toNumber(invoice.pharmacy_tax_amount),
			netTotal:
				toNumber(summary.pharmacy_total ?? summary.pharmacy_net_total) ||
				toNumber(invoice.pharmacy_total_amount),
			taxable: toNumber(summary.pharmacy_subtotal ?? summary.pharmacy_taxable_total ?? 0),
		};
	}

	return { discount: 0, tax: 0, netTotal: 0 };
};

export const toInputDateValue = (dateString) => {
	if (!dateString) return "";
	const date = new Date(dateString);
	if (Number.isNaN(date.getTime())) return "";
	return date.toISOString().split("T")[0];
};

export const getTodayDateString = () => {
	const today = new Date();
	const year = today.getFullYear();
	const month = String(today.getMonth() + 1).padStart(2, "0");
	const day = String(today.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

const toNumber = (value, fallback = 0) => {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : fallback;
};

export const getPatientDisplayName = (patient) => {
	if (!patient) return "-";
	if (patient.name) return patient.name;
	const parts = [patient.first_name, patient.middle_name, patient.last_name]
		.filter(Boolean)
		.map((part) => String(part).trim())
		.filter(Boolean);
	return parts.length > 0 ? parts.join(" ") : patient.full_name || "-";
};

export const hasBillingPurchase = (record) => {
	const billing = record?.billing;
	if (!billing || typeof billing !== "object") return false;
	return Object.keys(billing).length > 0;
};

/** True when billing has at least one displayable line (matches Collect Payment). */
export const hasBillingLineItems = (record) => {
	const billing = record?.billing;
	if (!billing || typeof billing !== "object") return false;
	return (
		(billing.consultation || []).length > 0 ||
		(billing.general_services || []).length > 0 ||
		(billing.investigations || []).length > 0 ||
		(billing.pharmacy_items || []).length > 0 ||
		(billing.treatment_package || []).length > 0
	);
};

/** Stamp sale date on pharmacy_items; used by pay-remaining / collect payment payloads. */
export const withPharmacyItemSaleDate = (items = [], saleDate = getTodayDateString()) =>
	(items || []).map((item) => ({
		...item,
		date: item.date || item.sale_date || saleDate,
	}));

export const isExistingPharmaSaleRecord = (record) => {
	if (!record) return false;
	if (record.salesId || record.sale_id || record.sales?._id) return true;
	return hasBillingPurchase(record);
};

const PHARMACY_SUMMARY_KEYS = [
	"pharmacy_subtotal",
	"pharmacy_discount",
	"pharmacy_net_taxable",
	"pharmacy_tax",
	"pharmacy_CGST",
	"pharmacy_SGST",
	"pharmacy_IGST",
	"pharmacy_VAT",
	"pharmacy_discount_type",
	"pharmacy_discount_value",
	"pharmacy_total",
	"pharmacy_taxable_total",
	"pharmacy_line_total",
	"pharmacy_net_total",
];

const hasPharmacyServiceLines = (services) =>
	(Array.isArray(services) ? services : []).some(
		(item) => String(item?.service_type || "").toLowerCase() === "pharmacy"
	);

/**
 * Pharmacy already on the sale/invoice (Details refresh OR local state after pay).
 * Used so later Pay Remaining calls send pharmacy_items: [] without requiring refresh.
 */
export const isPharmacyAlreadyBilledForPayRemaining = (record) => {
	if (!record) return false;
	if ((record?.billing?.pharmacy_items || []).length > 0) return true;
	if (hasPharmacyServiceLines(record?.appointment_invoice?.services)) return true;
	if (hasPharmacyServiceLines(record?.package_payment_details?.payment?.services)) {
		return true;
	}
	if (
		hasPharmacyServiceLines(
			record?.package_payment_details?.treatment_package_payment?.invoice?.services
		)
	) {
		return true;
	}

	const summary = record?.billing?.summary || {};
	return (
		toNumber(summary.pharmacy_total) > 0 ||
		toNumber(summary.pharmacy_subtotal) > 0 ||
		toNumber(summary.pharmacy_net_total) > 0
	);
};

/**
 * Collect Payment "pay remaining only": do not resend pharmacy_items when they
 * were already billed. Keep pharmacy summary totals from Details/List billing.
 */
export const resolvePayRemainingPharmacyPayload = (billingPayload, record) => {
	const payloadItems = billingPayload?.pharmacy_items || [];
	const alreadyBilled = isPharmacyAlreadyBilledForPayRemaining(record);

	const payloadSummary = billingPayload?.summary || {};
	const recordSummary = record?.billing?.summary || {};
	const summary = { ...payloadSummary };

	PHARMACY_SUMMARY_KEYS.forEach((key) => {
		const recordValue = recordSummary[key];
		const payloadValue = payloadSummary[key];
		if (recordValue !== undefined && recordValue !== null && recordValue !== "") {
			summary[key] = recordValue;
		} else if (payloadValue !== undefined && payloadValue !== null && payloadValue !== "") {
			summary[key] = payloadValue;
		}
	});

	return {
		pharmacy_items: alreadyBilled
			? []
			: withPharmacyItemSaleDate(payloadItems),
		summary,
	};
};

/** Keep existing billing line arrays when pay-remaining API returns empty ones. */
export const mergePayRemainingBilling = (previousBilling = {}, salesBilling = {}) => {
	const merged = { ...(previousBilling || {}), ...(salesBilling || {}) };
	const lineKeys = [
		"pharmacy_items",
		"consultation",
		"investigations",
		"general_services",
		"treatment_package",
	];

	lineKeys.forEach((key) => {
		const previousLines = previousBilling?.[key];
		const nextLines = salesBilling?.[key];
		if (
			Array.isArray(previousLines) &&
			previousLines.length > 0 &&
			(!Array.isArray(nextLines) || nextLines.length === 0)
		) {
			merged[key] = previousLines;
		}
	});

	const previousSummary = previousBilling?.summary || {};
	const nextSummary = salesBilling?.summary || {};
	if (Object.keys(previousSummary).length > 0 || Object.keys(nextSummary).length > 0) {
		const summary = { ...previousSummary, ...nextSummary };
		PHARMACY_SUMMARY_KEYS.forEach((key) => {
			const previousValue = previousSummary[key];
			const nextValue = nextSummary[key];
			const nextMissing =
				nextValue === undefined || nextValue === null || nextValue === "";
			if (
				nextMissing &&
				previousValue !== undefined &&
				previousValue !== null &&
				previousValue !== ""
			) {
				summary[key] = previousValue;
			}
		});
		merged.summary = summary;
	}

	return merged;
};

export const requiresPayRemainingBillingPayload = () => true;

export const getPatientAccountPatientId = (record) =>
	record?.patient_id ||
	record?.patient?._id ||
	record?.patient?.id ||
	record?.appointment_invoice?.patient_id ||
	"";

export const hasNonEmptyPayRemainingBillingSection = (billing) => {
	if (!billing || typeof billing !== "object") return false;
	const keys = [
		"consultation",
		"investigations",
		"general_services",
		"pharmacy_items",
		"treatment_package",
	];
	return keys.some((key) => Array.isArray(billing[key]) && billing[key].length > 0);
};

export const validatePayRemainingNewSalePayload = ({
	appointmentId,
	paidAmount,
	paymentType,
	patientId,
	patientName,
	billing,
	t,
}) => {
	const translate = (message) => (typeof t === "function" ? t(message) : message);
	const errors = {};

	if (!appointmentId) {
		errors.appointmentId = translate("Appointment not found for this invoice.");
	}
	if (!paymentType) {
		errors.paymentType = translate("Payment method is required.");
	}
	if (!paidAmount || toNumber(paidAmount) <= 0) {
		errors.paidAmount = translate("Amount must be greater than zero.");
	}
	if (!patientId) {
		errors.patientId = translate("Patient is required.");
	}
	if (!String(patientName || "").trim()) {
		errors.patientName = translate("Patient name is required.");
	}
	if (!billing) {
		errors.billing = translate("Billing details are required.");
	} else if (!hasNonEmptyPayRemainingBillingSection(billing)) {
		errors.billing = translate("At least one billing section is required.");
	}

	return errors;
};

export const formatBillingForPayRemainingSales = (billingPayload, record, options = {}) => {
	if (!billingPayload || !record) return null;

	const appointmentId = getCollectPaymentAppointmentId(record);
	const normalized = normalizePatientAccountItem(record);
	const appointmentInvoice = record?.appointment_invoice || {};
	const isTreatment =
		billingPayload.is_treatment_package === true || isTreatmentPackageInvoice(record);
	const currency =
		options.currency ||
		billingPayload.summary?.currency ||
		normalized?.currency ||
		appointmentInvoice.currency ||
		"INR";
	const paidSoFar = toNumber(normalized?.paid_amount);
	const grandTotal = toNumber(
		billingPayload.summary?.grand_total ?? normalized?.invoice_amount ?? appointmentInvoice.net_total
	);
	const pendingAmount = toNumber(
		normalized?.due_amount ?? Math.max(0, grandTotal - paidSoFar)
	);
	const { pharmacy_items: pharmacyItems, summary: pharmacyAwareSummary } =
		resolvePayRemainingPharmacyPayload(billingPayload, record);

	// Treatment package: keep POS-shaped billing (full invoice + treatment extras + current payment)
	if (isTreatment) {
		const packageSubTotal = toNumber(
			pharmacyAwareSummary?.package_sub_total ??
				pharmacyAwareSummary?.sub_total ??
				pharmacyAwareSummary?.subtotal
		);
		const treatmentPackageTotal = toNumber(
			pharmacyAwareSummary?.treatment_package_total ??
				billingPayload?.treatment_package?.[0]?.package_price
		);
		const servicesTotal = toNumber(pharmacyAwareSummary?.services_total);
		const investigationTotal = toNumber(pharmacyAwareSummary?.investigation_total);
		const resolvedSubTotal = toNumber(
			packageSubTotal ||
				treatmentPackageTotal + servicesTotal + investigationTotal
		);

		return {
			...billingPayload,
			appointment_id: billingPayload.appointment_id || appointmentId,
			patient_id: billingPayload.patient_id || getPatientAccountPatientId(record),
			doctor_id:
				billingPayload.doctor_id ||
				appointmentInvoice.doctor_id ||
				record?.doctor?._id ||
				null,
			appointment_invoice:
				billingPayload.appointment_invoice || appointmentInvoice || {
					appointment_id: appointmentId,
					invoice_number: appointmentInvoice.invoice_number || normalized?.invoice_no,
					invoice_id: appointmentInvoice._id,
					status: appointmentInvoice.status || normalized?.status || "unpaid",
				},
			consultation: billingPayload.consultation || [],
			investigations: billingPayload.investigations || [],
			general_services: billingPayload.general_services || [],
			pharmacy_items: pharmacyItems,
			treatment_package: billingPayload.treatment_package || [],
			summary: {
				...pharmacyAwareSummary,
				sub_total: resolvedSubTotal,
				subtotal: resolvedSubTotal,
				package_sub_total:
					toNumber(pharmacyAwareSummary?.package_sub_total) || resolvedSubTotal,
				grand_total: grandTotal,
				currency: pharmacyAwareSummary?.currency || currency,
			},
			payment: {
				...(billingPayload.payment || {}),
				currency: billingPayload.payment?.currency || currency,
				pending_amount: toNumber(
					billingPayload.payment?.pending_amount ?? pendingAmount
				),
				due_amount: toNumber(
					billingPayload.payment?.due_amount ??
					billingPayload.payment?.pending_amount ??
					pendingAmount
				),
			},
		};
	}

	return {
		...billingPayload,
		appointment_id: billingPayload.appointment_id || appointmentId,
		patient_id: billingPayload.patient_id || getPatientAccountPatientId(record),
		doctor_id:
			billingPayload.doctor_id ||
			appointmentInvoice.doctor_id ||
			record?.doctor?._id ||
			null,
		appointment_invoice: {
			appointment_id: appointmentId,
			invoice_number: appointmentInvoice.invoice_number || normalized?.invoice_no,
			invoice_id: appointmentInvoice._id,
			status: appointmentInvoice.status || normalized?.status || "unpaid",
		},
		consultation: billingPayload.consultation || [],
		investigations: billingPayload.investigations || [],
		general_services: billingPayload.general_services || [],
		pharmacy_items: pharmacyItems,
		summary: {
			...pharmacyAwareSummary,
			grand_total: grandTotal,
			currency,
		},
		payment: {
			paid_amount: paidSoFar,
			pending_amount: pendingAmount,
			due_amount: pendingAmount,
			currency,
		},
	};
};

const isDueAmountConsistent = (invoiceAmount, paidAmount, dueAmount) => {
	if (dueAmount <= 0) return true;
	return Math.abs(paidAmount + dueAmount - invoiceAmount) < 1;
};

const resolveBaseCurrency = (appointmentInvoice, billing) =>
	appointmentInvoice?.currency ||
	billing?.summary?.currency ||
	billing?.payment?.base_currency ||
	"INR";

const resolvePaymentHistoryList = (record) => {
	const appointmentHistory = record?.appointment_invoice?.payment_history || [];
	if (appointmentHistory.length > 0) return appointmentHistory;
	const billingHistory = record?.billing?.payment?.payment_history || [];
	if (billingHistory.length > 0) return billingHistory;
	const packageHistory = record?.package_payment_details?.payment_history || [];
	if (packageHistory.length > 0) return packageHistory;
	return record?.payment_history || [];
};

const resolveStoredExchangeRate = (item) => {
	const direct = toNumber(
		item?.exchange_rate ??
			item?.conversion_rate ??
			item?.currency_rate ??
			item?.rate ??
			item?.zigConversionValue
	);
	if (direct > 0) return direct;
	return 0;
};

const resolveStoredCurrencyRates = (item) =>
	item?.currencyRates ||
	item?.currency_rates ||
	item?.conversion_rates ||
	item?.rates ||
	null;

/**
 * Resolve collected vs base amounts for a payment_history row.
 * Prefer stored base equivalent / exchange rate on the transaction so display
 * stays consistent even if current POS rates have changed.
 */
export const resolvePaymentHistoryAmounts = (item, baseCurrency) => {
	const collected =
		item?.collected_currency || item?.currency || baseCurrency || "";
	const paidAmount = toNumber(item?.paid_amount);
	const receivedAmount = toNumber(
		item?.amount_received ?? item?.collected_amount ?? item?.paid_in_currency
	);
	const explicitBase = toNumber(
		item?.base_paid_amount ??
			item?.paid_amount_base ??
			item?.base_amount ??
			item?.converted_amount ??
			item?.converted_to_base
	);
	const exchangeRate = resolveStoredExchangeRate(item);
	const currencyRates = resolveStoredCurrencyRates(item);
	const isForeign = Boolean(collected && baseCurrency && collected !== baseCurrency);

	if (!isForeign) {
		const amount = paidAmount > 0 ? paidAmount : receivedAmount;
		return {
			collectedCurrency: collected || baseCurrency,
			baseCurrency,
			collectedAmount: amount,
			baseAmount: amount,
			exchangeRate: exchangeRate || null,
			isForeign: false,
		};
	}

	const collectedAmount = receivedAmount > 0 ? receivedAmount : paidAmount;

	let baseAmount = 0;
	if (explicitBase > 0) {
		baseAmount = explicitBase;
	} else if (
		receivedAmount > 0 &&
		paidAmount > 0 &&
		Math.abs(paidAmount - receivedAmount) > 0.005
	) {
		// amount_received in foreign currency; paid_amount is base equivalent
		baseAmount = paidAmount;
	} else if (
		currencyRates &&
		currencyRates[collected] != null &&
		currencyRates[baseCurrency] != null &&
		collectedAmount > 0
	) {
		baseAmount = toNumber(
			convertCurrencyEngine(
				collectedAmount,
				collected,
				baseCurrency,
				currencyRates
			).toNumber()
		);
	} else if (exchangeRate > 0 && collectedAmount > 0) {
		// Stored rate is units of base currency per 1 unit of collected currency
		baseAmount = toNumber(collectedAmount * exchangeRate);
	} else if (receivedAmount > 0 && paidAmount > 0) {
		baseAmount = paidAmount;
	}

	return {
		collectedCurrency: collected,
		baseCurrency,
		collectedAmount,
		baseAmount,
		exchangeRate: exchangeRate || null,
		isForeign: collectedAmount > 0,
	};
};

/** Compact money label: `$1` or `₹100` (whole numbers without .00). */
export const formatCompactCurrencyAmount = (amount, currency, getCurrencySymbol) => {
	const value = toNumber(amount);
	const formatted =
		Math.abs(value - Math.round(value)) < 0.005
			? String(Math.round(value))
			: value.toFixed(2);
	const symbol =
		(getCurrencySymbol && currency ? getCurrencySymbol(currency) : "") ||
		currency ||
		"";
	if (!symbol) return formatted;
	return `${symbol}${formatted}`;
};

/**
 * Paid Amount cell for payment history: `$1 (₹100)` when foreign, else base only.
 */
export const formatPaymentHistoryPaidAmount = (
	payment,
	getCurrencySymbol,
	fallbackBaseCurrency = "INR"
) => {
	if (!payment || payment.is_payment === false) return "";

	const baseCurrency =
		payment.base_currency || payment.baseCurrency || fallbackBaseCurrency;
	const collectedCurrency =
		payment.currency || payment.collected_currency || baseCurrency;
	const collectedAmount = toNumber(
		payment.collected_amount ?? payment.paid_amount ?? payment.amount
	);
	const baseAmount = toNumber(
		payment.base_paid_amount ?? payment.baseAmount ?? 0
	);
	const isForeign =
		payment.is_foreign === true ||
		(Boolean(collectedCurrency) &&
			Boolean(baseCurrency) &&
			collectedCurrency !== baseCurrency);

	if (!isForeign || collectedAmount <= 0) {
		return formatAmount(
			collectedAmount > 0 ? collectedAmount : baseAmount,
			baseCurrency || collectedCurrency,
			getCurrencySymbol
		);
	}

	const foreignLabel = formatCompactCurrencyAmount(
		collectedAmount,
		collectedCurrency,
		getCurrencySymbol
	);
	if (baseAmount <= 0) return foreignLabel;

	const baseLabel = formatCompactCurrencyAmount(
		baseAmount,
		baseCurrency,
		getCurrencySymbol
	);
	return `${foreignLabel} (${baseLabel})`;
};

export const getForeignPaymentSummary = (record, baseCurrency) => {
	const history = resolvePaymentHistoryList(record);
	const totals = {};

	history.forEach((item) => {
		const {
			collectedCurrency,
			collectedAmount,
			baseAmount,
			isForeign,
		} = resolvePaymentHistoryAmounts(item, baseCurrency);
		if (!isForeign || collectedAmount <= 0) return;
		if (!totals[collectedCurrency]) {
			totals[collectedCurrency] = { amount: 0, baseAmount: 0 };
		}
		totals[collectedCurrency].amount = toNumber(
			totals[collectedCurrency].amount + collectedAmount
		);
		totals[collectedCurrency].baseAmount = toNumber(
			totals[collectedCurrency].baseAmount + baseAmount
		);
	});

	return Object.entries(totals).map(([currency, values]) => ({
		currency,
		amount: values.amount,
		baseAmount: values.baseAmount,
		baseCurrency,
	}));
};

/**
 * Paid-column hint for foreign collections, e.g. `$1 (₹100)`.
 */
export const formatForeignPaymentSummary = (
	foreignPayments,
	getCurrencySymbol,
	baseCurrency = "INR"
) => {
	if (!foreignPayments?.length) return "";
	return foreignPayments
		.map((entry) => {
			const currency = entry.currency;
			const foreignLabel = formatCompactCurrencyAmount(
				entry.amount,
				currency,
				getCurrencySymbol
			);
			const baseCode = entry.baseCurrency || baseCurrency;
			const baseAmt = toNumber(entry.baseAmount);
			if (baseAmt <= 0 || !baseCode || baseCode === currency) {
				return foreignLabel;
			}
			const baseLabel = formatCompactCurrencyAmount(
				baseAmt,
				baseCode,
				getCurrencySymbol
			);
			return `${foreignLabel} (${baseLabel})`;
		})
		.join(" + ");
};

const isTreatmentPackageRecord = (record, appointmentInvoice = {}) =>
	record?.is_treatment_package === true ||
	appointmentInvoice?.invoice_type === "treatment_package" ||
	Boolean(record?.package_payment_details?.treatment_package);

/**
 * Apply section discount + tax the same way Collect Payment does
 * (calculateSectionBillingTotals): discount on subtotal, then tax on net taxable.
 */
const applySectionDiscountAndTax = (
	subtotal,
	discountType = "Percentage",
	discountValue = 0,
	taxPercentage = 0
) => {
	const base = toNumber(subtotal);
	const value = toNumber(discountValue);
	const rate = toNumber(taxPercentage);
	if (base <= 0) return 0;

	let discount = 0;
	if (value > 0) {
		const normalized = String(discountType || "Percentage").toLowerCase();
		discount =
			normalized === "fixed" || normalized === "flat"
				? toNumber(Math.min(base, value))
				: toNumber(Math.min(base, (base * value) / 100));
	}

	const netTaxable = toNumber(Math.max(0, base - discount));
	const tax = rate > 0 ? toNumber((netTaxable * rate) / 100) : 0;
	return toNumber(netTaxable + tax);
};

const resolveSectionDiscountParams = (summary, prefix, rawSubtotal) => {
	const rawType = summary?.[`${prefix}_discount_type`] || "Percentage";
	const normalized = String(rawType).toLowerCase();
	const discountType =
		normalized === "fixed" || normalized === "flat" ? "Fixed" : "Percentage";
	let discountValue = toNumber(summary?.[`${prefix}_discount_value`]);

	// Derive rate/amount from stored absolute discount when value is missing.
	if (discountValue <= 0) {
		const absDiscount = toNumber(summary?.[`${prefix}_discount`]);
		const summarySubtotal = toNumber(
			summary?.[`${prefix}_subtotal`] || (rawSubtotal > absDiscount ? rawSubtotal : 0)
		);
		if (absDiscount > 0) {
			if (discountType === "Fixed") {
				discountValue = absDiscount;
			} else if (summarySubtotal > 0) {
				discountValue = toNumber((absDiscount / summarySubtotal) * 100);
			}
		}
	}

	return { discountType, discountValue };
};

const resolveSectionTaxPercentage = (summary, prefix) => {
	const explicit = toNumber(
		summary?.[`${prefix}_tax_percentage`] ?? summary?.[`${prefix}_tax_rate`]
	);
	if (explicit > 0) return explicit;

	const tax = toNumber(summary?.[`${prefix}_tax`]);
	if (tax <= 0) return 0;

	const netTaxable = toNumber(summary?.[`${prefix}_net_taxable`]);
	if (netTaxable > 0) return toNumber((tax / netTaxable) * 100);

	// Derive rate when net_taxable is missing: section total ≈ net + tax.
	const sectionTotal = toNumber(summary?.[`${prefix}_total`]);
	if (sectionTotal > tax) {
		const derivedNet = toNumber(sectionTotal - tax);
		if (derivedNet > 0) return toNumber((tax / derivedNet) * 100);
	}

	const subtotal = toNumber(summary?.[`${prefix}_subtotal`]);
	const discount = toNumber(summary?.[`${prefix}_discount`]);
	const approxNet = toNumber(Math.max(0, subtotal - discount));
	if (approxNet > 0) return toNumber((tax / approxNet) * 100);

	return 0;
};

/**
 * Live Invoice Total matching Collect Payment composition.
 * Recalculates services/lab with the same discount type/value + tax rate applied
 * in the Collect Payment modal (including newly added session services).
 */
const computeLiveBillableTotal = (record, appointmentInvoice, billing) => {
	const billingSummary = billing?.summary || null;
	const billingPayment = billing?.payment || null;
	const summaryGrand = toNumber(
		billingSummary?.grand_total ?? billingPayment?.grand_total ?? billingPayment?.net_total
	);
	const apiTotal = toNumber(
		appointmentInvoice.billable_amount ??
			appointmentInvoice.net_total ??
			record?.invoice_amount
	);

	const invoiceServices = appointmentInvoice.services || [];
	const consultationServices = record?.consultation?.additional_services || [];
	const serviceSource =
		invoiceServices.length > 0 ? invoiceServices : consultationServices;

	let generalServicesTotal = 0;
	let pharmacyServicesTotal = 0;
	let laboratoryServicesTotal = 0;
	serviceSource.forEach((item) => {
		const type = String(item.service_type || "general").toLowerCase();
		const qty = Math.max(1, Number(item.quantity) || 1);
		const unitPrice = toNumber(item.price ?? item.charges);
		const lineTotal = toNumber(item.amount ?? unitPrice * qty);
		if (type === "pharmacy") {
			pharmacyServicesTotal += lineTotal;
		} else if (type === "laboratory" || type === "lab") {
			laboratoryServicesTotal += lineTotal;
		} else if (type !== "treatment_package") {
			generalServicesTotal += lineTotal;
		}
	});

	const summaryPharmacy = toNumber(
		billingSummary?.pharmacy_total ?? billingSummary?.pharmacy_net_total
	);
	const billingPharmacyTotal =
		summaryPharmacy > 0
			? summaryPharmacy
			: (billing?.pharmacy_items || []).reduce(
					(sum, item) =>
						sum + toNumber(item.net_total ?? item.amount ?? item.sub_total ?? 0),
					0
				);
	const billingServicesTotal = [
		...(billing?.consultation || []),
		...(billing?.general_services || []),
	].reduce(
		(sum, item) => sum + toNumber(item.sub_total ?? item.amount ?? 0),
		0
	);
	const billingLabTotal = (billing?.investigations || []).reduce(
		(sum, item) => sum + toNumber(item.sub_total ?? item.amount ?? 0),
		0
	);

	const rawServices =
		invoiceServices.length > 0
			? generalServicesTotal
			: billingServicesTotal || generalServicesTotal;
	const rawLab =
		laboratoryServicesTotal > 0
			? laboratoryServicesTotal
			: billingLabTotal;
	const pharmacyTotal = billingPharmacyTotal || pharmacyServicesTotal;

	const packagePrice = toNumber(
		record?.package_payment_details?.treatment_package?.total_price ??
			record?.consultation?.package_price ??
			appointmentInvoice.package_price
	);

	const servicesDiscount = resolveSectionDiscountParams(
		billingSummary,
		"services",
		rawServices || toNumber(billingSummary?.services_subtotal)
	);
	const labDiscount = resolveSectionDiscountParams(
		billingSummary,
		"investigation",
		rawLab || toNumber(billingSummary?.investigation_subtotal)
	);
	const servicesTaxPct = resolveSectionTaxPercentage(billingSummary, "services");
	const labTaxPct = resolveSectionTaxPercentage(billingSummary, "investigation");

	// Collect Payment: section nets after discount + tax on current line subtotals.
	const servicesNet =
		rawServices > 0
			? applySectionDiscountAndTax(
					rawServices,
					servicesDiscount.discountType,
					servicesDiscount.discountValue,
					servicesTaxPct
				)
			: toNumber(billingSummary?.services_total);
	const labNet =
		rawLab > 0
			? applySectionDiscountAndTax(
					rawLab,
					labDiscount.discountType,
					labDiscount.discountValue,
					labTaxPct
				)
			: toNumber(billingSummary?.investigation_total);

	const liveTotal = toNumber(packagePrice + servicesNet + labNet + pharmacyTotal);
	if (liveTotal > 0) return liveTotal;
	if (summaryGrand > 0) return summaryGrand;
	return apiTotal;
};

const resolvePatientAccountAmounts = (record, appointmentInvoice, billing) => {
	const isSettled = isAppointmentInvoiceSettled(appointmentInvoice);
	const baseCurrency = resolveBaseCurrency(appointmentInvoice, billing);
	const paymentStatus = normalizePaymentStatus(
		appointmentInvoice.status || billing?.payment?.status || record?.status
	);
	const billingPayment = billing?.payment || null;

	// Collect Payment "Invoice Total": live composition so new session services
	// raise Invoice Amount / Due Amount before payment updates billing.summary.
	const invoiceAmount = computeLiveBillableTotal(record, appointmentInvoice, billing);

	// Collect Payment "Paid" (paidSoFar): cumulative collected total.
	// billing.payment.paid_amount is often only the last installment.
	const paidFromHistory = (
		appointmentInvoice.payment_history ||
		billingPayment?.payment_history ||
		record.payment_history ||
		[]
	).reduce((sum, item) => sum + toNumber(item?.paid_amount), 0);

	const paidAmount = toNumber(
		appointmentInvoice.paid_amount ??
			record.paid_amount ??
			(paidFromHistory > 0 ? paidFromHistory : null) ??
			billingPayment?.paid_amount
	);

	// Collect Payment "Balance Due": always grandTotal - paidSoFar.
	const computedDue = Math.max(0, invoiceAmount - paidAmount);

	if (isSettled || paymentStatus === "paid") {
		return {
			invoice_amount: invoiceAmount,
			paid_amount: paidAmount,
			due_amount: 0,
			pending_amount: 0,
			base_currency: baseCurrency,
		};
	}

	const billingDue = billingPayment?.due_amount;
	const billingPending = billingPayment?.pending_amount;
	// Prefer API due when it matches Invoice Total − Paid (same as Collect Payment).
	const apiDue = toNumber(
		billingDue ??
			appointmentInvoice.due_amount ??
			record.due_amount ??
			billingPending ??
			record.pending_amount
	);

	let dueAmount;
	if (paidAmount + 0.01 >= invoiceAmount) {
		dueAmount = 0;
	} else if (apiDue > 0 && isDueAmountConsistent(invoiceAmount, paidAmount, apiDue)) {
		dueAmount = apiDue;
	} else {
		dueAmount = computedDue;
	}

	return {
		invoice_amount: invoiceAmount,
		paid_amount: paidAmount,
		due_amount: dueAmount,
		pending_amount: dueAmount,
		base_currency: baseCurrency,
	};
};

export const normalizePatientAccountItem = (record) => {
	if (!record) return null;

	const appointmentInvoice = record.appointment_invoice || {};
	const billing = hasBillingPurchase(record) ? record.billing : null;
	const amounts = resolvePatientAccountAmounts(record, appointmentInvoice, billing);
	const baseCurrency = amounts.base_currency || resolveBaseCurrency(appointmentInvoice, billing);
	const foreignPayments = getForeignPaymentSummary(record, baseCurrency);

	return {
		...record,
		_recordId: record._id,
		invoiceId: appointmentInvoice._id || record._id,
		invoice_no: appointmentInvoice.invoice_number || record.invoice_no || "",
		invoice_number: appointmentInvoice.invoice_number || record.invoice_no || "",
		invoice_date: appointmentInvoice.createdAt || record.createdAt || record.invoice_date,
		currency: baseCurrency,
		base_currency: baseCurrency,
		is_conversion: appointmentInvoice.is_conversion === true || foreignPayments.length > 0,
		foreign_payments: foreignPayments,
		status:
			appointmentInvoice.status ||
			billing?.payment?.status ||
			record.status,
		invoice_amount: amounts.invoice_amount,
		paid_amount: amounts.paid_amount,
		due_amount: amounts.due_amount,
		pending_amount: amounts.pending_amount,
		is_settled: appointmentInvoice.is_settled === true,
		payment_history:
			appointmentInvoice.payment_history ||
			billing?.payment?.payment_history ||
			record.payment_history ||
			[],
	};
};

export const getCollectPaymentAppointmentId = (record) =>
	record?.appointment_invoice?.appointment_id ||
	record?.billing?.appointment_id ||
	record?.appointment_id ||
	"";

export const buildPayRemainingSalesPayload = ({
	appointmentId,
	paidAmount,
	paymentType = "cash",
	currencyType = "INR",
	isSettled = false,
	isConversion = false,
	invoiceRecord,
	billing,
	patientId,
	patientName,
	items,
	netTotal,
	doctorId,
}) => {
	const payload = {
		appointment_id: appointmentId,
		paidAmount: toNumber(paidAmount),
		paymentType,
		currencyType,
		isSettled: Boolean(isSettled),
		isConversion: Boolean(isConversion),
		billing,
		patientId: patientId || getPatientAccountPatientId(invoiceRecord),
		patientName: patientName || getPatientDisplayName(invoiceRecord?.patient),
		customerType: "hospital_patient",
		isHospitalConnected: true,
	};

	const resolvedDoctorId =
		doctorId ||
		billing?.doctor_id ||
		invoiceRecord?.appointment_invoice?.doctor_id ||
		invoiceRecord?.doctor?._id;

	if (resolvedDoctorId) {
		payload.doctorId = resolvedDoctorId;
	}

	if (Array.isArray(items) && items.length > 0) {
		payload.items = items;
		if (netTotal != null) {
			payload.netTotal = toNumber(netTotal);
		}
	}

	return payload;
};

export const applyPayRemainingSalesResponse = (record, apiData, submittedBilling) => {
	if (!record || !apiData?.sales) return record;

	const sales = apiData.sales;
	const salesBilling = sales.billing || {};
	const payment = salesBilling.payment || {};
	const appointmentInvoice = record.appointment_invoice || {};

	const paidAmount = toNumber(
		payment.paid_amount ?? sales.totalPaid ?? appointmentInvoice.paid_amount
	);
	const dueAmount = toNumber(
		payment.due_amount ??
		sales.dueAmount ??
		appointmentInvoice.due_amount ??
		payment.pending_amount
	);
	const isSettled =
		sales.isSettled === true || appointmentInvoice.is_settled === true;
	const paymentStatus =
		payment.status || sales.paymentStatus || appointmentInvoice.status;

	let updatedBilling =
		Object.keys(salesBilling).length > 0
			? mergePayRemainingBilling(record.billing || {}, salesBilling)
			: record.billing;

	// If API omits pharmacy lines, keep what we just billed so the next Pay Remaining
	// (without page refresh) still treats pharmacy as already billed.
	const submittedPharmacy = submittedBilling?.pharmacy_items || [];
	if (
		submittedPharmacy.length > 0 &&
		(!Array.isArray(updatedBilling?.pharmacy_items) ||
			updatedBilling.pharmacy_items.length === 0)
	) {
		updatedBilling = mergePayRemainingBilling(updatedBilling || {}, {
			pharmacy_items: submittedPharmacy,
			summary: submittedBilling?.summary || {},
		});
	}

	return {
		...record,
		salesId: sales._id || record.salesId,
		billing: updatedBilling,
		appointment_invoice: {
			...appointmentInvoice,
			paid_amount: paidAmount,
			due_amount: isSettled ? 0 : dueAmount,
			status: paymentStatus,
			is_settled: isSettled,
			payment_history:
				payment.payment_history || appointmentInvoice.payment_history,
			invoice: sales.invoiceUrl || appointmentInvoice.invoice,
			receipt: sales.receiptUrl || appointmentInvoice.receipt,
			...(salesBilling.invoice_id ? { invoice_id: salesBilling.invoice_id } : {}),
		},
	};
};

export const getPatientAccountRecordStorageKey = (invoiceId) =>
	`patient-account-record-${invoiceId}`;

export const savePatientAccountRecord = (invoiceId, record) => {
	if (!invoiceId || !record) return;
	try {
		sessionStorage.setItem(
			getPatientAccountRecordStorageKey(invoiceId),
			JSON.stringify(record)
		);
	} catch (error) {
		console.error("Failed to cache patient account record:", error);
	}
};

export const loadPatientAccountRecord = (invoiceId, locationState) => {
	if (locationState?.record) {
		return locationState.record;
	}

	if (!invoiceId) return null;

	try {
		const cached = sessionStorage.getItem(getPatientAccountRecordStorageKey(invoiceId));
		return cached ? JSON.parse(cached) : null;
	} catch (error) {
		console.error("Failed to read cached patient account record:", error);
		return null;
	}
};

export const getPatientAccountInvoiceId = (record) =>
	record?.appointment_invoice?._id || record?.invoiceId || record?._id || "";

/**
 * Score pharmacy lines by billing detail richness (tax / taxable / net).
 * List endpoints often return qty+MRP only; details keep tax split for Collect Payment.
 */
export const scorePharmacyItemsBillingDetail = (items = []) =>
	(items || []).reduce((score, item) => {
		if (!item || typeof item !== "object") return score;
		let lineScore = 1;
		const subTotal = toNumber(item.sub_total);
		const netTotal = toNumber(item.net_total ?? item.amount);
		const taxAmount = toNumber(item.tax_amount);
		const taxField = toNumber(item.tax);
		const taxPct = toNumber(item.tax_percentage);
		if (subTotal > 0) lineScore += 2;
		if (netTotal > 0) lineScore += 1;
		if (taxAmount > 0 || (taxField > 0 && taxPct > 0)) lineScore += 3;
		else if (taxField > 0 || taxPct > 0) lineScore += 2;
		if (subTotal > 0 && netTotal > subTotal) lineScore += 2;
		return score + lineScore;
	}, 0);

/**
 * Prefer the richest invoice record when opening POS Dispense (list row + details cache).
 * Ensures previously dispensed pharmacy_items survive re-dispense navigation.
 */
export const mergeDispensePrescriptionData = (listItem, cachedRecord) => {
	if (!listItem && !cachedRecord) return null;
	if (!cachedRecord) return listItem;
	if (!listItem) return cachedRecord;

	const cachedPharmacy = cachedRecord?.billing?.pharmacy_items || [];
	const listPharmacy = listItem?.billing?.pharmacy_items || [];
	const cachedPharmacyScore = scorePharmacyItemsBillingDetail(cachedPharmacy);
	const listPharmacyScore = scorePharmacyItemsBillingDetail(listPharmacy);
	const preferCachedPharmacy =
		cachedPharmacyScore > listPharmacyScore ||
		(cachedPharmacy.length > listPharmacy.length && cachedPharmacyScore >= listPharmacyScore) ||
		(cachedPharmacy.length > 0 && listPharmacy.length === 0);
	const preferCachedBilling =
		preferCachedPharmacy ||
		(cachedPharmacy.length === 0 &&
			listPharmacy.length === 0 &&
			Object.keys(cachedRecord?.billing || {}).length >
				Object.keys(listItem?.billing || {}).length);

	const listPrescriptions = Array.isArray(listItem.prescriptions)
		? listItem.prescriptions
		: [];
	const cachedPrescriptions = Array.isArray(cachedRecord.prescriptions)
		? cachedRecord.prescriptions
		: [];

	const mergedBilling = preferCachedBilling
		? { ...(listItem.billing || {}), ...(cachedRecord.billing || {}) }
		: { ...(cachedRecord.billing || {}), ...(listItem.billing || {}) };

	// Always keep the richer pharmacy_items array even when other billing keys merge.
	if (preferCachedPharmacy && cachedPharmacy.length > 0) {
		mergedBilling.pharmacy_items = cachedPharmacy;
	} else if (!preferCachedPharmacy && listPharmacy.length > 0) {
		mergedBilling.pharmacy_items = listPharmacy;
	}

	return {
		...listItem,
		...cachedRecord,
		_id: listItem._id || cachedRecord._id,
		prescriptions:
			listPrescriptions.length > 0 ? listPrescriptions : cachedPrescriptions,
		patient: listItem.patient || cachedRecord.patient,
		appointment_invoice: {
			...(listItem.appointment_invoice || {}),
			...(cachedRecord.appointment_invoice || {}),
		},
		billing: mergedBilling,
		package_payment_details:
			cachedRecord.package_payment_details || listItem.package_payment_details,
	};
};

const mapInvoiceServiceToDetailLine = (item, index) => {
	const quantity = Math.max(1, Number(item.quantity) || 1);
	const lineAmount = toNumber(item.amount);
	const price = toNumber(
		item.price ?? item.charges ?? (lineAmount > 0 ? lineAmount / quantity : 0)
	);
	return {
		id: item._id || item.service_id || `invoice-service-${index}`,
		service_name: item.service_name || item.name || "-",
		service_type: item.service_type || "general",
		quantity,
		charges: price,
		// Prefer invoice line amount so Details matches Collect Payment totals.
		amount: toNumber(lineAmount || price * quantity),
	};
};

export const buildPatientAccountDetailLines = (record) => {
	if (!record) return [];

	// Same source priority as Collect Payment: real billing lines first, else invoice services.
	if (hasBillingLineItems(record)) {
		const billing = record.billing;
		const lines = [];

		(billing.consultation || []).forEach((item, index) => {
			const quantity = Math.max(1, Number(item.quantity) || 1);
			const unitPrice = toNumber(item.price ?? item.unit_price ?? item.amount);
			// Match Collect Payment pre-tax line amount (sub_total), not net_total.
			lines.push({
				id: `consultation-${index}`,
				service_name: item.consultation_name || "Consultation",
				service_type: "consultation",
				quantity,
				charges: unitPrice,
				amount: toNumber(item.sub_total ?? item.amount ?? unitPrice * quantity),
			});
		});

		(billing.general_services || []).forEach((item, index) => {
			const quantity = Math.max(1, Number(item.quantity) || 1);
			const unitPrice = toNumber(
				item.price ?? item.unit_price ?? (item.amount ? item.amount / quantity : 0)
			);
			lines.push({
				id: item.service_id || `service-${index}`,
				service_name: item.service_name || "Service",
				service_type: "general",
				quantity,
				charges: unitPrice,
				amount: toNumber(item.sub_total ?? item.amount ?? unitPrice * quantity),
			});
		});

		(billing.investigations || []).forEach((item, index) => {
			const quantity = Math.max(1, Number(item.quantity) || 1);
			const unitPrice = toNumber(item.price ?? item.unit_price ?? item.amount);
			lines.push({
				id: item.test_id || `investigation-${index}`,
				service_name: item.test_name || "Investigation",
				service_type: item.type || "laboratory",
				quantity,
				charges: unitPrice,
				amount: toNumber(item.sub_total ?? item.amount ?? unitPrice * quantity),
			});
		});

		(billing.pharmacy_items || []).forEach((item, index) => {
			const quantity = Math.max(1, Number(item.qty ?? item.quantity) || 1);
			const unitPrice = toNumber(item.unit_price ?? item.price);
			lines.push({
				id: item.item_id || `pharmacy-${index}`,
				service_name: item.item || item.brand_name || "Pharmacy Item",
				service_type: "pharmacy",
				quantity,
				charges: unitPrice,
				amount: toNumber(item.net_total ?? item.amount ?? item.sub_total ?? unitPrice * quantity),
			});
		});

		return lines;
	}

	const invoiceServices = record?.appointment_invoice?.services || [];
	if (invoiceServices.length > 0) {
		return invoiceServices.map(mapInvoiceServiceToDetailLine);
	}

	const consultation = record?.consultation || {};
	const fallbackLines = [];
	const consultationCharge = toNumber(consultation.consultation_charge);
	if (consultationCharge > 0 || consultation.package_name) {
		const quantity = Math.max(1, Number(consultation.quantity) || 1);
		const unitPrice = toNumber(
			consultation.package_price ?? consultation.consultation_charge
		);
		fallbackLines.push({
			id: `consultation-${record._id || "fallback"}`,
			service_name: consultation.package_name || consultation.consultation_reason || "Consultation",
			service_type: "consultation",
			quantity,
			charges: unitPrice,
			amount: toNumber(unitPrice * quantity),
		});
	}

	(consultation.additional_services || []).forEach((item, index) => {
		fallbackLines.push(mapInvoiceServiceToDetailLine(item, index));
	});

	return fallbackLines;
};

export const buildPatientAccountFinancialSummary = (record) => {
	const normalized = normalizePatientAccountItem(record);
	const appointmentInvoice = record?.appointment_invoice || {};
	const billingSummary = hasBillingPurchase(record) ? record.billing?.summary || {} : {};
	const baseCurrency = normalized?.base_currency || normalized?.currency || "INR";
	const invoiceTotal = toNumber(normalized?.invoice_amount);
	const hasBillingSummary = Object.keys(billingSummary).length > 0;
	const billingGrandTotal = toNumber(billingSummary.grand_total);
	// Only trust billing section totals when real line items also exist (same as Collect Payment).
	const useBillingSections = hasBillingLineItems(record) && hasBillingSummary && billingGrandTotal > 0;

	const lines = buildPatientAccountDetailLines(record);
	const lineTotalByTypes = (types) =>
		lines
			.filter((line) => types.includes(line.service_type))
			.reduce((sum, line) => sum + toNumber(line.amount), 0);

	const sectionDiscount = toNumber(
		(billingSummary.services_discount || 0) +
			(billingSummary.investigation_discount || 0) +
			(billingSummary.pharmacy_discount || 0)
	);

	const servicesTotal = useBillingSections
		? toNumber(billingSummary.services_total)
		: lineTotalByTypes(["consultation", "general"]) ||
			toNumber(appointmentInvoice.consultation_total_amount);
	const investigationTotal = useBillingSections
		? toNumber(billingSummary.investigation_total)
		: lineTotalByTypes(["laboratory", "lab"]) ||
			toNumber(appointmentInvoice.laboratory_total_amount);
	const pharmacyTotal = useBillingSections
		? toNumber(billingSummary.pharmacy_total ?? billingSummary.pharmacy_net_total)
		: lineTotalByTypes(["pharmacy"]) || toNumber(appointmentInvoice.pharmacy_total_amount);

	const linesSubtotal = lines.reduce((sum, line) => sum + toNumber(line.amount), 0);
	const sectionsSubtotal = toNumber(servicesTotal + investigationTotal + pharmacyTotal);

	// billing.summary.tax from buildBillingSummary is pharmacy-only; always prefer
	// the sum of section taxes so services/lab tax is not dropped (especially when
	// pharmacy tax is 0 and `??` would keep that 0).
	const sectionTax = toNumber(
		toNumber(billingSummary.services_tax) +
			toNumber(billingSummary.investigation_tax) +
			toNumber(billingSummary.pharmacy_tax)
	);
	const resolvedTax = toNumber(
		sectionTax || billingSummary.tax || appointmentInvoice.tax_amount
	);

	// billing.summary.discount is also pharmacy-only from buildBillingSummary.
	const resolvedDiscount = toNumber(
		sectionDiscount || billingSummary.discount || appointmentInvoice.discount
	);

	const resolvedSubTotal = toNumber(
		useBillingSections
			? linesSubtotal || sectionsSubtotal || billingSummary.grand_total
			: linesSubtotal ||
					appointmentInvoice.sub_total ||
					billingSummary.subtotal ||
					invoiceTotal
	);

	// invoiceTotal comes from normalizePatientAccountItem → computeLiveBillableTotal
	// (same Invoice Total as Collect Payment / Payment List).
	const resolvedBillable = toNumber(
		invoiceTotal ||
			billingSummary.grand_total ||
			appointmentInvoice.billable_amount ||
			appointmentInvoice.net_total
	);

	// Prefer API/billable total; otherwise derive so Subtotal - Discount + Tax matches Net Total.
	const resolvedNetTotal = toNumber(
		resolvedBillable ||
			Math.round((resolvedSubTotal - resolvedDiscount + resolvedTax) * 100) / 100
	);

	return {
		currency: baseCurrency,
		sub_total: resolvedSubTotal,
		discount: resolvedDiscount,
		billable_amount: resolvedBillable || resolvedNetTotal,
		net_total: resolvedNetTotal,
		total_paid: toNumber(normalized?.paid_amount),
		due: toNumber(normalized?.due_amount ?? normalized?.pending_amount),
		services_total: servicesTotal,
		investigation_total: investigationTotal,
		pharmacy_total: pharmacyTotal,
		tax: resolvedTax,
		foreign_payments: normalized?.foreign_payments || [],
	};
};

export const isTreatmentPackageInvoice = (record) =>
	isTreatmentPackageRecord(record, record?.appointment_invoice || {});

export const formatGender = (gender) => {
	if (!gender) return "-";
	const normalized = String(gender).toLowerCase();
	if (normalized === "male") return "Male";
	if (normalized === "female") return "Female";
	return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

export const formatTreatmentSessionSchedule = (session, sessionDetail) => {
	const appointment = sessionDetail?.session?.appointment;
	if (appointment?.appointment_date && appointment?.start_time && appointment?.end_time) {
		return `${formatDateList(appointment.appointment_date)} ${appointment.start_time} - ${appointment.end_time}`;
	}
	if (session?.schedule) return session.schedule;
	if (session?.session_date_time) return formatDateList(session.session_date_time);
	return "-";
};

export const buildTreatmentPackageSessionRows = (record) => {
	const pkgDetails = record?.package_payment_details || {};
	const sessions = pkgDetails.treatment_package?.sessions || [];
	const sessionDetails = pkgDetails.session_details || [];
	const detailsBySessionId = {};

	sessionDetails.forEach((detail) => {
		const sessionId = detail?.session?._id;
		if (sessionId) detailsBySessionId[sessionId] = detail;
	});

	return sessions.map((session, index) => ({
		id: session._id || `session-${index}`,
		index: index + 1,
		sessionName: session.session_name || "-",
		description: session.description || session.session_name || "-",
		duration: session.duration || "-",
		schedule: formatTreatmentSessionSchedule(session, detailsBySessionId[session._id]),
		status: String(session.status || "pending").toLowerCase(),
	}));
};

export const buildTreatmentPackageDetails = (record) => {
	const pkgDetails = record?.package_payment_details || {};
	const treatmentPackage = pkgDetails.treatment_package || {};
	const appointmentInvoice = record?.appointment_invoice || {};
	const payment = pkgDetails.payment || appointmentInvoice;
	const consultation = record?.consultation || {};
	const firstSession = treatmentPackage.sessions?.[0];
	const sessionDetail = pkgDetails.session_details?.[0];
	const billing = hasBillingLineItems(record) ? record.billing : null;

	const invoiceServices = appointmentInvoice.services || [];
	const consultationServices = consultation.additional_services || [];
	const serviceSource =
		invoiceServices.length > 0 ? invoiceServices : consultationServices;

	let generalServicesTotal = 0;
	let pharmacyServicesTotal = 0;
	let laboratoryServicesTotal = 0;
	serviceSource.forEach((item) => {
		const type = String(item.service_type || "general").toLowerCase();
		const qty = Math.max(1, Number(item.quantity) || 1);
		const unitPrice = toNumber(item.price ?? item.charges);
		const lineTotal = toNumber(item.amount ?? unitPrice * qty);
		if (type === "pharmacy") {
			pharmacyServicesTotal += lineTotal;
		} else if (type === "laboratory" || type === "lab") {
			laboratoryServicesTotal += lineTotal;
		} else if (type !== "treatment_package") {
			generalServicesTotal += lineTotal;
		}
	});

	const billingPharmacyFromSummary = toNumber(
		billing?.summary?.pharmacy_total ?? billing?.summary?.pharmacy_net_total ?? 0
	);
	const billingPharmacyTotal =
		billingPharmacyFromSummary > 0
			? billingPharmacyFromSummary
			: (billing?.pharmacy_items || []).reduce(
					(sum, item) =>
						sum + toNumber(item.net_total ?? item.amount ?? item.sub_total ?? 0),
					0
				);
	const billingServicesTotal = [
		...(billing?.consultation || []),
		...(billing?.general_services || []),
	].reduce(
		(sum, item) => sum + toNumber(item.sub_total ?? item.amount ?? 0),
		0
	);
	const billingLabTotal = (billing?.investigations || []).reduce(
		(sum, item) => sum + toNumber(item.sub_total ?? item.amount ?? 0),
		0
	);

	const additionalServicesTotal =
		invoiceServices.length > 0 ? generalServicesTotal : billingServicesTotal || generalServicesTotal;
	const pharmacyTotal =
		billingPharmacyTotal > 0 ? billingPharmacyTotal : pharmacyServicesTotal;
	const laboratoryTotal =
		billingLabTotal > 0
			? billingLabTotal
			: invoiceServices.length > 0
				? laboratoryServicesTotal
				: laboratoryServicesTotal;

	const packagePrice = toNumber(
		treatmentPackage.total_price ??
			consultation.package_price ??
			appointmentInvoice.package_price
	);

	// Same Invoice Total as Collect Payment / Payment List (discount-aware).
	const totalPayable = computeLiveBillableTotal(
		record,
		appointmentInvoice,
		hasBillingPurchase(record) ? record.billing : billing
	);

	const totalCollected = toNumber(payment.paid_amount ?? appointmentInvoice.paid_amount);
	const remainingBalance = isAppointmentInvoiceSettled(appointmentInvoice)
		? 0
		: Math.max(0, totalPayable - totalCollected);

	const sessions = buildTreatmentPackageSessionRows(record);
	const completedSessions = sessions.filter(
		(session) => String(session.status || "").toLowerCase() === "completed"
	).length;

	return {
		packageName:
			treatmentPackage.package_name ||
			consultation.package_name ||
			appointmentInvoice.package_name ||
			"-",
		packagePrice,
		additionalServicesTotal,
		laboratoryTotal,
		pharmacyTotal,
		totalPayable,
		totalCollected,
		remainingBalance,
		noOfSessions:
			treatmentPackage.no_of_sessions ??
			consultation.no_of_sessions ??
			appointmentInvoice.no_of_sessions ??
			sessions.length ??
			0,
		completedSessions,
		currentSession:
			pkgDetails.current_session?.session_name ||
			sessionDetail?.session?.session_name ||
			"-",
		treatmentStartDate:
			firstSession?.session_date_time ||
			treatmentPackage.createdAt ||
			record?.createdAt ||
			"",
		clinicName:
			record?.hospital?.name ||
			record?.clinic?.name ||
			record?.appointment?.hospital_name ||
			record?.appointment?.clinic_name ||
			"",
		currency: payment.currency || appointmentInvoice.currency || consultation.currency || "INR",
		sessions,
	};
};

const TREATMENT_NON_SERVICE_TYPES = new Set([
	"pharmacy",
	"laboratory",
	"lab",
	"treatment_package",
]);

export const isTreatmentBillableServiceType = (serviceType) =>
	!TREATMENT_NON_SERVICE_TYPES.has(String(serviceType || "general").toLowerCase());

export const isTreatmentPendingService = (item) =>
	String(item?.status || "pending").toLowerCase() === "pending";

export const buildTreatmentAdditionalServices = (record, options = {}) => {
	const {
		billableOnly = false,
		excludePharmacyAndLab = false,
		// Deprecated: appointment_invoice.services always wins when present so
		// Treatment & Other Services and Additional Services stay synchronized.
		preferConsultationSource = false,
	} = options;

	const invoiceServices = record?.appointment_invoice?.services || [];
	const consultationServices = record?.consultation?.additional_services || [];
	const addedServices =
		record?.package_payment_details?.treatment_package_payment?.added_services || [];

	// Prefer appointment_invoice.services whenever present (source of truth for amounts).
	let source = [];
	if (invoiceServices.length > 0) {
		source = invoiceServices;
	} else if (preferConsultationSource && consultationServices.length > 0) {
		source = consultationServices;
	} else if (consultationServices.length > 0) {
		source = consultationServices;
	} else if (addedServices.length > 0) {
		source = addedServices;
	}

	const baseCurrency = record?.appointment_invoice?.currency || "INR";

	return source
		.filter((item) => {
			const serviceType = String(item.service_type || "general").toLowerCase();
			if (excludePharmacyAndLab || billableOnly) {
				if (!isTreatmentBillableServiceType(serviceType)) return false;
			}
			if (billableOnly) {
				return isTreatmentPendingService(item);
			}
			return true;
		})
		.map((item, index) => {
			const quantity = Math.max(1, Number(item.quantity) || 1);
			const lineAmount = toNumber(item.amount);
			const price = toNumber(
				item.price ??
				item.charges ??
				(lineAmount > 0 ? lineAmount / quantity : 0)
			);
			const serviceType = String(item.service_type || "general").toLowerCase();
			return {
				id: item._id || item.service_id || `service-${index}`,
				service_id: item.service_id || "",
				service_name: item.service_name || item.name || "-",
				service_type: item.service_type || "general",
				quantity,
				unit_price: price,
				price,
				amount: toNumber(lineAmount || price * quantity),
				status: item.status || "pending",
				currency: item.currency || baseCurrency,
				isConsultation: serviceType === "consultation",
			};
		});
};

export const buildTreatmentInvestigationItems = (record) => {
	const baseCurrency = record?.appointment_invoice?.currency || "INR";

	const mapLabLine = (item, index, {
		idPrefix = "lab",
		nameKey = "test_name",
	} = {}) => {
		const quantity = Math.max(1, Number(item.quantity) || 1);
		const lineAmount = toNumber(item.amount);
		const unitPrice = toNumber(
			item.price ??
				item.unit_price ??
				item.charges ??
				(lineAmount > 0 ? lineAmount / quantity : 0)
		);
		// Prefer billed pre-tax line amount (same as Collect Payment), then net / raw.
		const amount = toNumber(
			item.sub_total ?? (lineAmount || unitPrice * quantity)
		);
		const name =
			item[nameKey] ||
			item.test_name ||
			item.service_name ||
			item.name ||
			"-";

		return {
			id: item.test_id || item._id || item.service_id || `${idPrefix}-${index}`,
			test_id: item.test_id || item.service_id || item._id,
			test_name: name,
			name,
			type: item.type || item.service_type || "laboratory",
			quantity,
			unit_price: unitPrice,
			price: unitPrice,
			amount,
			currency: item.currency || baseCurrency,
			status: item.status || "pending",
		};
	};

	const isLabServiceType = (serviceType) => {
		const type = String(serviceType || "").toLowerCase();
		return type === "laboratory" || type === "lab";
	};

	// 1) appointment_invoice.services where service_type is laboratory (source of truth)
	const billingInvestigations = record?.billing?.investigations || [];
	const billingByTestId = new Map(
		billingInvestigations.map((item) => [
			String(item.test_id || item.service_id || item._id),
			item,
		])
	);

	const fromInvoice = (record?.appointment_invoice?.services || [])
		.filter((item) => isLabServiceType(item.service_type))
		.map((item, index) => {
			const serviceId = String(item.service_id || item._id || "");
			const billingLine = billingByTestId.get(serviceId);
			if (billingLine) {
				return mapLabLine(billingLine, index, {
					idPrefix: "billing-investigation",
					nameKey: "test_name",
				});
			}
			return mapLabLine(item, index, { idPrefix: "invoice-lab", nameKey: "service_name" });
		});
	if (fromInvoice.length > 0) return fromInvoice;

	// 2) Collect Payment / billed investigations (no invoice lab lines)
	if (hasBillingLineItems(record) && billingInvestigations.length > 0) {
		return billingInvestigations.map((item, index) =>
			mapLabLine(item, index, { idPrefix: "billing-investigation" })
		);
	}

	// 3) consultation.additional_services laboratory (when investigation[] is empty)
	const fromConsultation = (record?.consultation?.additional_services || [])
		.filter((item) => isLabServiceType(item.service_type))
		.map((item, index) =>
			mapLabLine(item, index, {
				idPrefix: "consultation-lab",
				nameKey: "service_name",
			})
		);
	if (fromConsultation.length > 0) return fromConsultation;

	// EMR `investigation[]` examinations are doctor suggestions only — do not auto-load
	// them into the Lab Investigations billable table (Collect Payment shows them as Suggested).
	return [];
};

const formatHistoryServiceLine = (service, currency, getCurrencySymbol) => {
	const name = service?.service_name || service?.name || "Service";
	const price = toNumber(service?.price ?? service?.unit_price ?? service?.amount);
	const priceLabel = formatAmount(
		price,
		service?.currency || currency,
		getCurrencySymbol
	);
	return `${name} (${priceLabel})`;
};

/**
 * Maps package_payment_details.payment_history into Treatment & Payment History rows:
 * - session_id → Session & Additional Services (S1: name; + service lines when present)
 * - paid_amount > 0 (no session) → Payment / Payment Collected
 * - services only → Additional Service
 */
export const buildTreatmentPackagePaymentHistory = (record, getCurrencySymbol) => {
	const pkgDetails = record?.package_payment_details || {};
	const appointmentInvoice = record?.appointment_invoice || {};
	const baseCurrency =
		pkgDetails.payment?.currency || appointmentInvoice.currency || "INR";
	const history =
		pkgDetails.payment_history?.length > 0
			? pkgDetails.payment_history
			: appointmentInvoice.payment_history || [];

	return history
		.filter((item) => {
			if (!item) return false;
			if (item.session_id) return true;
			if (toNumber(item.paid_amount) > 0) return true;
			if (
				toNumber(
					item.amount_received ?? item.collected_amount ?? item.paid_in_currency
				) > 0
			) {
				return true;
			}
			return Array.isArray(item.services) && item.services.length > 0;
		})
		.map((item) => {
			const {
				collectedCurrency,
				baseCurrency: resolvedBase,
				collectedAmount,
				baseAmount,
				exchangeRate,
				isForeign,
			} = resolvePaymentHistoryAmounts(item, baseCurrency);
			const paidAmount = toNumber(item.paid_amount);
			const hasSession = Boolean(item.session_id);
			const services = Array.isArray(item.services) ? item.services : [];
			const date = item.createdAt || item.payment_date;

			if (hasSession) {
				const sessionNumber = item.session_number ?? item.session?.session_number;
				const sessionName =
					item.session_name || item.session?.session_name || "";
				const sessionLabel = sessionNumber
					? `S${sessionNumber}: ${sessionName}`.trim()
					: sessionName || "Session";
				const serviceLines = services.map((service) =>
					formatHistoryServiceLine(service, collectedCurrency, getCurrencySymbol)
				);

				return {
					date,
					type: "Session & Additional Services",
					description: [sessionLabel, ...serviceLines].filter(Boolean).join("\n"),
					paid_amount: null,
					collected_amount: null,
					base_paid_amount: null,
					currency: collectedCurrency,
					base_currency: resolvedBase,
					exchange_rate: exchangeRate,
					is_foreign: false,
					payment_mode: null,
					is_payment: false,
				};
			}

			if (paidAmount > 0 || collectedAmount > 0) {
				return {
					date,
					type: "Payment",
					description: item.description || item.notes || "Payment Collected",
					paid_amount: collectedAmount,
					collected_amount: collectedAmount,
					base_paid_amount: baseAmount,
					currency: collectedCurrency,
					base_currency: resolvedBase,
					exchange_rate: exchangeRate,
					is_foreign: isForeign && baseAmount > 0,
					payment_mode: item.payment_mode || "-",
					is_payment: true,
				};
			}

			return {
				date,
				type: "Additional Service",
				description: services
					.map((service) =>
						formatHistoryServiceLine(service, collectedCurrency, getCurrencySymbol)
					)
					.join("\n") || "-",
				paid_amount: null,
				collected_amount: null,
				base_paid_amount: null,
				currency: collectedCurrency,
				base_currency: resolvedBase,
				exchange_rate: exchangeRate,
				is_foreign: false,
				payment_mode: null,
				is_payment: false,
			};
		});
};

export const buildPatientAccountPaymentHistory = (record) => {
	const appointmentInvoice = record?.appointment_invoice || {};
	const baseCurrency =
		appointmentInvoice.currency || record?.billing?.payment?.base_currency || "INR";
	const appointmentHistory = appointmentInvoice.payment_history || [];
	const billingHistory = record?.billing?.payment?.payment_history || [];

	const source =
		appointmentHistory.length > 0
			? appointmentHistory
			: billingHistory.map((item) => ({
				...item,
				payment_mode: item.payment_mode,
				createdAt: item.createdAt,
			}));

	return source
		.filter((item) => toNumber(item?.paid_amount) > 0 || item?.payment_mode)
		.map((item) => {
			const {
				collectedCurrency,
				baseCurrency: resolvedBase,
				collectedAmount,
				baseAmount,
				exchangeRate,
				isForeign,
			} = resolvePaymentHistoryAmounts(item, baseCurrency);
			return {
				payment_date: item.createdAt || item.payment_date,
				paid_amount: collectedAmount,
				collected_amount: collectedAmount,
				base_paid_amount: baseAmount,
				currency: collectedCurrency,
				base_currency: resolvedBase,
				exchange_rate: exchangeRate,
				is_foreign: isForeign && baseAmount > 0,
				payment_mode: item.payment_mode || item.payment_status || "-",
				receipt_number: item.receipt_number || "",
			};
		});
};
